import React from 'react';
import { Box, IconButton, Tooltip } from '@chakra-ui/react';
import { DragHandleIcon, CopyIcon, DeleteIcon } from '@chakra-ui/icons';
import { useDrag, useDrop } from 'react-dnd';
import { rowStyle } from './componentStyles';
import DroppableColumn from './DroppableColumn';
import { useEditorStore } from '../editorStore';

// Utility Functions
const moveRow = (dragIndex, hoverIndex, moveRowInStore) => {
  moveRowInStore(dragIndex, hoverIndex);
};

// Droppable Row Component
const DroppableRow = ({ row, parentId, index, moveRow, syncEditorToHtml, onSelect, selectedTarget }) => {
  const duplicateRowInStore = useEditorStore((state) => state.duplicateRow);
  const removeRowInStore = useEditorStore((state) => state.removeRow);
  const moveRowInStore = useEditorStore((state) => state.moveRow);

  const [, drag] = useDrag({
    type: 'ROW',
    item: { index },
  });

  const [, drop] = useDrop({
    accept: 'ROW',
    hover: (item) => {
      if (item.index !== index) {
        moveRow(item.index, index, moveRowInStore);
        item.index = index;
      }
    },
  });

  // Function to duplicate the row
  const duplicateRow = () => {
    duplicateRowInStore(parentId, row);
  };

  const isSelected = selectedTarget?.kind === 'row' && selectedTarget?.id === row.id;

  const handleSelectRow = (e) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect({
        kind: 'row',
        id: row.id,
        data: row,
      });
    }
  };

  // Merge row.settings into editor display
  const rowSettingsStyle = (() => {
    const s = row?.settings || {};
    const out = {};
    // Always apply background color from settings
    if (s.backgroundColor && s.backgroundColor !== 'transparent') {
      out.backgroundColor = s.backgroundColor;
    }
    // Apply background image
    if (s.backgroundImage) {
      out.backgroundImage = `url('${s.backgroundImage}')`;
      out.backgroundSize = s.backgroundSize || 'cover';
      out.backgroundPosition = s.backgroundPosition || 'center';
      out.backgroundRepeat = s.backgroundRepeat || 'no-repeat';
    }
    if (s.padding && typeof s.padding === 'object') {
      const { top = 0, right = 0, bottom = 0, left = 0 } = s.padding;
      if (top || right || bottom || left) {
        out.padding = `${top}px ${right}px ${bottom}px ${left}px`;
      }
    }
    if (s.margin && typeof s.margin === 'object') {
      const { top = 0, right = 0, bottom = 0, left = 0 } = s.margin;
      if (top || right || bottom || left) {
        out.margin = `${top}px ${right}px ${bottom}px ${left}px`;
      }
    }
    // Do NOT apply imported border styles to editor rows — they come from HTML source
    // elements like table wrappers and produce wrong white/colored borders in the editor.
    if (s.textAlign) {
      out.textAlign = s.textAlign;
    }
    if (s.boxSizing) {
      out.boxSizing = s.boxSizing;
    }
    // Do NOT apply imported width/height/maxWidth to editor rows — rows should always
    // span full width. Explicit pixel values from HTML table attrs collapse
    // the row to a tiny size in the editor's flex layout.
    if (s.minHeight) {
      out.minHeight = s.minHeight;
    }
    if (s.color) {
      out.color = s.color;
    }
    if (s.textColor) {
      out.color = s.textColor;
    }
    if (s.fontFamily) {
      out.fontFamily = s.fontFamily;
    }
    if (s.fontSize) {
      out.fontSize = s.fontSize;
    }
    if (s.fontWeight) {
      out.fontWeight = s.fontWeight;
    }
    if (s.lineHeight) {
      out.lineHeight = s.lineHeight;
    }
    if (s.letterSpacing) {
      out.letterSpacing = s.letterSpacing;
    }
    if (s.borderRadius) {
      out.borderRadius = `${s.borderRadius}px`;
    } else if (s.borderRadius === 0) {
      out.borderRadius = '0px';
    }
    // Note: don't apply float or display from imported settings — they break
    // the flex-based editor row layout (rowStyle uses display:flex).
    if (s.justifyContent) {
      out.justifyContent = s.justifyContent;
    }
    if (s.alignItems) {
      out.alignItems = s.alignItems;
    }
    if (s.flexWrap) {
      out.flexWrap = s.flexWrap;
    }
    if (s.flexDirection) {
      out.flexDirection = s.flexDirection;
    }
    if (s.overflow) {
      out.overflow = s.overflow;
    }
    return out;
  })();

  return (
    <Box
      ref={(node) => drag(drop(node))}
      onClick={handleSelectRow}
      _hover={{ outline: isSelected ? '2px solid #3182ce' : '1px dashed #a0aec0' }}
      style={{
        ...rowStyle,
        ...rowSettingsStyle,
        justifyContent: row?.settings?.justifyContent || 'flex-start',
        alignItems: row?.settings?.alignItems || 'stretch',
        outline: isSelected ? '2px solid #3182ce' : 'none',
      }}
    >
      <Box position="absolute" top="2px" left="2px" zIndex={5} opacity={isSelected ? 1 : 0.35} _hover={{ opacity: 1 }} transition="opacity 0.15s ease">
        <Tooltip label="Drag to reorder row" fontSize="xs" openDelay={400}>
          <DragHandleIcon cursor="grab" color="gray.500" />
        </Tooltip>
      </Box>
      {row.columns.map((col) => (
        <DroppableColumn
          key={col.id}
          column={col}
          colSpan={col.size}
          parentId={parentId}
          rowId={row.id}
          syncEditorToHtml={syncEditorToHtml} // Pass syncEditorToHtml explicitly
          onSelect={onSelect}
          selectedTarget={selectedTarget}
        />
      ))}
      <Box position="absolute" top="2px" right="2px" zIndex={5} opacity={isSelected ? 1 : 0.35} _hover={{ opacity: 1 }} transition="opacity 0.15s ease">
        <Tooltip label="Duplicate row" fontSize="xs" openDelay={400}>
          <IconButton
            aria-label="Duplicate row"
            onClick={(e) => {
              e.stopPropagation();
              duplicateRow();
            }}
            colorScheme="blue"
            variant="solid"
            size="xs"
            icon={<CopyIcon />}
            ml={2}
          />
        </Tooltip>
        <Tooltip label="Delete row" fontSize="xs" openDelay={400}>
          <IconButton
            aria-label="Delete row"
            onClick={(e) => {
              e.stopPropagation();
              removeRowInStore(parentId, row.id);
            }}
            colorScheme="red"
            variant="solid"
            size="xs"
            icon={<DeleteIcon />}
            ml={2}
          />
        </Tooltip>
      </Box>
    </Box>
  );
};

export default DroppableRow;
export { moveRow };
